import React, { memo, useEffect, useMemo, useState } from 'react'
import {
  BankFilled,
  CalendarFilled,
  CrownFilled,
  HeartFilled,
  HomeFilled,
  IdcardFilled,
  MailFilled,
  MobileFilled,
  PhoneFilled,
} from '@ant-design/icons'
import { Avatar, Col, Row } from 'antd'
import { FaUser } from 'react-icons/fa'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import PropTypes from 'prop-types'

import utils from '../../../utils'
import { apiUrl } from '../../../config'
import { UserDetailContent, UserDetailItem } from './ListDepartmentStyled'
import { getContactInitialName } from '../contactConstants'

const DetailItem = memo(({ icon, label, value }) => {
  const isEmpty = value === undefined || value === null || value === ''

  return (
    <UserDetailItem>
      <div className="detail-icon-wrapper">{icon}</div>
      <div className="detail-info">
        <div className={`detail-value ${isEmpty ? 'empty' : ''}`}>
          {isEmpty ? 'Chưa cập nhật' : value}
        </div>
        <span className="detail-label">{label}</span>
      </div>
    </UserDetailItem>
  )
})

DetailItem.propTypes = {
  icon: PropTypes.node,
  label: PropTypes.string,
  value: PropTypes.node,
}

const UserDetail = userDetailProps => {
  const { selectedUser, userStore } = userDetailProps
  const { userAvatarByCode, userAvatarByCodeOwner } = userStore
  const [avatarSrc, setAvatarSrc] = useState(undefined)
  const selectedUserCode = selectedUser?.code
  const selectedUsername = selectedUser?.username
  const displayName = useMemo(
    () =>
      selectedUser?.name_lowercase ||
      selectedUser?.name_uppercase ||
      selectedUser?.name ||
      'Chưa cập nhật',
    [
      selectedUser?.name_lowercase,
      selectedUser?.name_uppercase,
      selectedUser?.name,
    ]
  )
  const homePhone = useMemo(
    () =>
      selectedUser?.home_phone?.trim() ? selectedUser.home_phone : undefined,
    [selectedUser?.home_phone]
  )
  const birthday = useMemo(
    () =>
      selectedUser?.birthDate
        ? moment(selectedUser.birthDate).format('DD/MM/YYYY')
        : undefined,
    [selectedUser?.birthDate]
  )
  const address = useMemo(
    () => (selectedUser?.address?.trim() ? selectedUser.address : undefined),
    [selectedUser?.address]
  )
  const departmentName = useMemo(
    () =>
      selectedUser?.department?.name ||
      (typeof selectedUser?.department === 'string'
        ? selectedUser.department
        : undefined),
    [selectedUser?.department]
  )
  const positionName = useMemo(
    () =>
      selectedUser?.position?.name ||
      (typeof selectedUser?.position === 'string'
        ? selectedUser.position
        : undefined),
    [selectedUser?.position]
  )
  const aclGroupName = useMemo(
    () => selectedUser?.acl_group?.name || 'Chưa phân quyền',
    [selectedUser?.acl_group?.name]
  )
  const avatarImageSrc = useMemo(() => {
    if (avatarSrc) return avatarSrc
    if (selectedUser?.imageId) {
      return `${apiUrl}/api/v1/images/${selectedUser.imageId}`
    }
    return undefined
  }, [avatarSrc, selectedUser?.imageId])

  useEffect(() => {
    if (!userAvatarByCode || userAvatarByCodeOwner !== selectedUserCode) {
      setAvatarSrc(undefined)
      return undefined
    }

    const objectUrl = URL.createObjectURL(userAvatarByCode)
    setAvatarSrc(objectUrl)

    return () => {
      URL.revokeObjectURL(objectUrl)
    }
  }, [selectedUserCode, userAvatarByCode, userAvatarByCodeOwner])

  if (!selectedUsername) {
    return null
  }

  return (
    <UserDetailContent>
      <div className="profile-heading">
        <Avatar size={104} src={avatarImageSrc}>
          <span className="avatar-initial">
            {getContactInitialName(selectedUser)}
          </span>
        </Avatar>
        <h1>{displayName}</h1>
      </div>

      <Row className="detail-grid" gutter={[16, 12]}>
        <Col xs={24} sm={12}>
          <DetailItem icon={<FaUser />} label="Id" value={selectedUsername} />
        </Col>
        <Col xs={24} sm={12}>
          <DetailItem
            icon={<HeartFilled />}
            label="Giới tính"
            value={utils.getGenderName(selectedUser?.gender)}
          />
        </Col>
        <Col xs={24} sm={12}>
          <DetailItem
            icon={<MailFilled />}
            label="Email"
            value={selectedUser?.email}
          />
        </Col>
        <Col xs={24} sm={12}>
          <DetailItem
            icon={<BankFilled />}
            label="Phòng ban"
            value={departmentName}
          />
        </Col>
        <Col xs={24} sm={12}>
          <DetailItem
            icon={<IdcardFilled />}
            label="Mã nhân viên"
            value={selectedUser?.ma_nv}
          />
        </Col>
        <Col xs={24} sm={12}>
          <DetailItem
            icon={<CrownFilled />}
            label="Chức danh"
            value={positionName}
          />
        </Col>
        <Col xs={24} sm={12}>
          <DetailItem
            icon={<PhoneFilled />}
            label="Số máy nội bộ"
            value={homePhone}
          />
        </Col>
        <Col xs={24} sm={12}>
          <DetailItem
            icon={<CalendarFilled />}
            label="Ngày sinh"
            value={birthday}
          />
        </Col>
        <Col xs={24} sm={12}>
          <DetailItem
            icon={<MobileFilled />}
            label="Điện thoại di động"
            value={selectedUser?.phone}
          />
        </Col>
        <Col xs={24} sm={12}>
          <DetailItem icon={<HomeFilled />} label="Địa chỉ" value={address} />
        </Col>
        <Col xs={24} sm={12}>
          <DetailItem
            icon={<IdcardFilled />}
            label="Phân quyền"
            value={aclGroupName}
          />
        </Col>
      </Row>
    </UserDetailContent>
  )
}

UserDetail.propTypes = {
  selectedUser: PropTypes.object,
}

export default memo(inject('userStore')(observer(UserDetail)))
